import { strToU8, zipSync } from "fflate";
import { buildRankPack } from "../src/domain/rankpack";
import type { Grant, Rank } from "../src/domain/ranks";
import type { RankRecord } from "../src/http/rank-record";
import { api } from "./api";
import { h } from "./dom";
import { download } from "./export";
import { colorHex, rankGoalText, rankPerks } from "./rank-describe";

interface RanksState {
  grants: Grant[];
  builtIn: Rank[];
  custom: RankRecord[];
  draft: Rank | null;
  prompt: string;
  busy: boolean;
  status: { text: string; error: boolean };
  loaded: boolean;
}

const state: RanksState = {
  grants: [],
  builtIn: [], custom: [], draft: null, prompt: "", busy: false, status: { text: "", error: false }, loaded: false,
};

export function ranksView(parent: boolean, rerender: () => void): HTMLElement[] {
  if (!state.loaded) {
    state.loaded = true;
    void refresh(rerender);
  }
  return [
    designPanel(rerender),
    ...(parent ? [reviewPanel(rerender), giftPanel(rerender)] : []),
    storePanel(),
    installPanel(),
  ];
}

async function refresh(rerender: () => void) {
  try {
    const { builtIn, custom, grants } = await api.ranks();
    Object.assign(state, { builtIn, custom, grants });
  } catch (err) {
    state.status = { text: err instanceof Error ? err.message : "Couldn't load ranks.", error: true };
  }
  rerender();
}

async function act(rerender: () => void, task: () => Promise<string>) {
  state.busy = true;
  state.status = { text: "", error: false };
  rerender();
  try {
    state.status = { text: await task(), error: false };
  } catch (err) {
    state.status = { text: err instanceof Error ? err.message : "Something went wrong.", error: true };
  } finally {
    state.busy = false;
    rerender();
  }
}

function designPanel(rerender: () => void) {
  const box = h("textarea", { id: "rank-prompt", maxlength: 500, placeholder: "Example: THUNDERLORD, yellow, lightning sparkles, earned by beating 500 mobs" }) as HTMLTextAreaElement;
  box.value = state.prompt;
  box.addEventListener("input", () => { state.prompt = box.value; });
  return h("section", { class: "panel", "aria-labelledby": "rank-design-title" },
    h("h2", { id: "rank-design-title" }, "Design your own rank"),
    h("label", { class: "label", for: "rank-prompt" }, "Describe your rank: name, color, perks, and how to earn it"),
    box,
    h("div", { class: "btn-row" },
      h("button", {
        class: "btn btn-go", type: "button", disabled: state.busy,
        onclick: () => void act(rerender, async () => {
          if (!state.prompt.trim()) throw new Error("Describe your rank first!");
          state.draft = (await api.designRank(state.prompt.trim())).rank;
          return "Here's your rank! Send it to a grown-up to approve.";
        }),
      }, state.busy ? "Designing..." : "✨ Design my rank"),
    ),
    h("p", { class: `status${state.status.error ? " error" : ""}`, role: "status" }, state.status.text),
    state.draft ? h("div", { class: "rank-grid" }, rankCard(state.draft, null)) : null,
    state.draft ? h("div", { class: "btn-row" },
      h("button", {
        class: "btn btn-go", type: "button", disabled: state.busy,
        onclick: () => void act(rerender, async () => {
          await api.submitRank(state.draft!);
          state.draft = null;
          state.prompt = "";
          await refresh(rerender);
          return "Sent! A grown-up will check it.";
        }),
      }, "📨 Send for approval"),
      h("button", { class: "btn btn-danger", type: "button", onclick: () => { state.draft = null; rerender(); } }, "✖ Toss it"),
    ) : null,
  );
}

function reviewPanel(rerender: () => void) {
  const pending = state.custom.filter((r) => r.status === "pending");
  return h("section", { class: "panel", "aria-labelledby": "rank-review-title" },
    h("h2", { id: "rank-review-title" }, `Parent: ranks waiting for you (${pending.length})`),
    pending.length
      ? h("div", { class: "rank-grid" }, ...pending.map((record) => h("div", {},
        rankCard(record.rank, record),
        h("div", { class: "btn-row" },
          ...(["approve", "reject"] as const).map((decision) => h("button", {
            class: `btn btn-small${decision === "reject" ? " btn-danger" : " btn-go"}`, type: "button", disabled: state.busy,
            onclick: () => void act(rerender, async () => {
              await api.reviewRank(record.rank.id, decision);
              await refresh(rerender);
              return `${record.rank.name} ${decision === "approve" ? "approved" : "rejected"}.`;
            }),
          }, decision === "approve" ? "✔ Approve" : "✖ Reject")),
        ),
      )))
      : h("p", { class: "empty" }, "Nothing to review."),
  );
}

function giftPanel(rerender: () => void) {
  const ranks = [...state.builtIn, ...state.custom.filter((r) => r.status === "approved").map((r) => r.rank)];
  const select = h("select", { id: "gift-rank" }, ...ranks.map((r) => h("option", { value: r.id }, r.name))) as HTMLSelectElement;
  const player = h("input", { type: "text", id: "gift-player", placeholder: "Minecraft name, e.g. TheReximum5", maxlength: 17 }) as HTMLInputElement;
  const nameOf = (id: string) => ranks.find((r) => r.id === id)?.name ?? id;
  return h("section", { class: "panel", "aria-labelledby": "gift-title" },
    h("h2", { id: "gift-title" }, "Parent: give a rank as a gift"),
    h("div", { class: "gift-row" },
      h("label", { class: "label", for: "gift-rank" }, "Rank"), select,
      h("label", { class: "label", for: "gift-player" }, "Player"), player,
      h("button", {
        class: "btn btn-go btn-small", type: "button", disabled: state.busy,
        onclick: () => void act(rerender, async () => {
          await api.grantRank({ rankId: select.value, player: player.value.trim() });
          await refresh(rerender);
          return `${player.value.trim()} now has ${nameOf(select.value)}! Download the ranks pack again for the server.`;
        }),
      }, "🎁 Give rank"),
    ),
    state.grants.length
      ? h("ul", { class: "gift-list" }, ...state.grants.map((g) => h("li", {},
        `${nameOf(g.rankId)} → ${g.player} `,
        h("button", {
          class: "btn btn-small btn-danger", type: "button", disabled: state.busy,
          onclick: () => void act(rerender, async () => {
            await api.ungrantRank(g);
            await refresh(rerender);
            return `Removed ${nameOf(g.rankId)} from ${g.player}.`;
          }),
        }, "Remove"),
      )))
      : h("p", { class: "empty" }, "No gifts yet."),
  );
}

function storePanel() {
  const approved = state.custom.filter((r) => r.status === "approved");
  const mine = state.custom.filter((r) => r.status !== "approved");
  return h("section", { class: "panel", "aria-labelledby": "rank-store-title" },
    h("h2", { id: "rank-store-title" }, "Server ranks (earn them by playing!)"),
    h("div", { class: "rank-grid" },
      ...state.builtIn.map((rank) => rankCard(rank, null)),
      ...approved.map((record) => rankCard(record.rank, record)),
    ),
    mine.length ? h("h2", {}, "Your designs") : null,
    mine.length ? h("div", { class: "rank-grid" }, ...mine.map((record) => rankCard(record.rank, record))) : null,
    h("div", { class: "btn-row" },
      h("button", {
        class: "btn btn-go", type: "button",
        onclick: () => {
          const pack = buildRankPack([...state.builtIn, ...approved.map((r) => r.rank)], state.grants);
          download("FamilyRanks-DataPack.zip", zipSync(Object.fromEntries(Object.entries(pack).map(([p, t]) => [p, strToU8(t)]))));
        },
      }, "⬇ Download ranks pack"),
    ),
  );
}

function rankCard(rank: Rank, record: RankRecord | null) {
  const badge = record
    ? record.status === "approved" ? `Designed by ${record.designedBy}` : record.status === "pending" ? "Waiting for a grown-up" : "Not approved"
    : null;
  return h("article", { class: "rank-card tooltip" },
    h("p", { class: "rank-tag", style: `color:${colorHex(rank.color)};${rank.bold ? "font-weight:700" : ""}` }, `[${rank.name}]`),
    rank.motto ? h("p", { class: "lore" }, rank.motto) : null,
    h("p", { class: "section" }, "How to earn:"),
    h("p", { class: "ability" }, rankGoalText(rank)),
    h("p", { class: "section" }, "Perks:"),
    h("ul", {}, ...rankPerks(rank).map((perk) => h("li", { class: "power" }, perk))),
    badge ? h("p", { class: `rank-badge${record?.status === "approved" ? "" : " warn"}` }, badge) : null,
  );
}

function installPanel() {
  return h("section", { class: "panel guide", "aria-labelledby": "rank-install-title" },
    h("h2", { id: "rank-install-title" }, "Put ranks on the server"),
    h("ol", {},
      h("li", {}, "A grown-up clicks ", h("strong", {}, "Download ranks pack"), " and puts ", h("code", {}, "FamilyRanks-DataPack.zip"),
        " in the server's ", h("code", {}, "world/datapacks"), " folder, then runs ", h("code", {}, "/reload"), "."),
      h("li", {}, "In the game, type ", h("code", {}, "/trigger ranks_menu"), " to see your progress, and ", h("code", {}, "/trigger ranks_kit"), " once a day for kits."),
      h("li", {}, "Your best rank shows as a colored tag in chat and over your head. Perks from every rank you earn stack up!"),
      h("li", {}, "Progress counts from when the pack is installed."),
    ),
  );
}
